import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Copy, Search, Info, Plus, Layers } from "lucide-react";
import { mpApi, SERVICE_KEYS, serviceMeta, setServices, monthLabel, shiftMonth } from "./mpShared";

/** Configure each client's monthly deliverables per service. */
export default function TargetsTab({ month, businessId, canManage, onSaved, onAddClient, onManageServices, highlightClientId, refreshKey }) {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({}); // client_id -> targets
  const [scope, setScope] = useState("default"); // default | month
  const [saving, setSaving] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(() => mpApi("/targets", { params: { month, business_id: businessId || undefined } })
    .then((d) => { setServices(d.services); setData(d); setEdits({}); }).catch((e) => toast.error(e.message)), [month, businessId]);
  useEffect(() => { load(); }, [load, highlightClientId, refreshKey]);

  const rows = useMemo(() => {
    if (!data) return [];
    const s = q.trim().toLowerCase();
    return data.clients.filter((c) => (showAll || c.total > 0 || edits[c.client_id] || c.client_id === highlightClientId) && (!s || c.name.toLowerCase().includes(s)))
      .sort((a, b) => (b.client_id === highlightClientId) - (a.client_id === highlightClientId));
  }, [data, showAll, q, edits, highlightClientId]);

  const valueOf = (c, k) => (edits[c.client_id] ? (edits[c.client_id][k] ?? 0) : (c.targets[k] ?? 0));
  const setVal = (c, k, v) => setEdits((e) => ({ ...e, [c.client_id]: { ...(e[c.client_id] || c.targets), [k]: Math.max(0, Math.min(999, Number(v) || 0)) } }));

  const saveRow = async (c) => {
    setSaving(c.client_id);
    try {
      await mpApi("/targets", { method: "PUT", body: { client_id: c.client_id, targets: edits[c.client_id], scope, month, business_id: businessId || undefined } });
      toast.success(`${c.name}: targets saved${scope === "month" ? ` for ${monthLabel(month)} only` : " for every month"}`);
      await load(); onSaved?.();
    } catch (e) { toast.error(e.message); } finally { setSaving(""); }
  };
  const saveAll = async () => {
    for (const c of data.clients.filter((x) => edits[x.client_id])) await saveRow(c); // eslint-disable-line no-await-in-loop
  };
  const copyPrevious = async () => {
    const from = shiftMonth(month, -1);
    if (!window.confirm(`Copy every client's targets from ${monthLabel(from)} into ${monthLabel(month)} (this month only)?`)) return;
    try { const r = await mpApi("/targets/copy", { method: "POST", body: { from, to: month, business_id: businessId || undefined } }); toast.success(`Copied targets for ${r.clients} client(s)`); await load(); onSaved?.(); }
    catch (e) { toast.error(e.message); }
  };

  const dirty = Object.keys(edits).length;
  const colTotal = (k) => rows.reduce((s, c) => s + (Number(valueOf(c, k)) || 0), 0);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client…" className="h-9 w-56 rounded-lg border border-slate-200 pl-8 pr-3 text-sm" />
          </div>
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} /> Show clients without targets</label>
        </div>
        {canManage && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-lg border border-slate-200 bg-white p-0.5 text-xs">
              <button onClick={() => setScope("default")} className={`rounded-md px-2.5 py-1.5 font-medium ${scope === "default" ? "bg-slate-900 text-white" : "text-slate-600"}`}>Save as default (every month)</button>
              <button onClick={() => setScope("month")} className={`rounded-md px-2.5 py-1.5 font-medium ${scope === "month" ? "bg-slate-900 text-white" : "text-slate-600"}`}>{monthLabel(month)} only</button>
            </div>
            {onManageServices && <button onClick={onManageServices} data-testid="mp-targets-services" className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"><Layers className="h-3.5 w-3.5" /> Add / manage services</button>}
            {onAddClient && <button onClick={onAddClient} data-testid="mp-targets-add-client" className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700"><Plus className="h-3.5 w-3.5" /> Add client</button>}
            <button onClick={copyPrevious} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"><Copy className="h-3.5 w-3.5" /> Copy previous month</button>
            {dirty > 0 && <button onClick={saveAll} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700"><Save className="h-3.5 w-3.5" /> Save {dirty} change{dirty === 1 ? "" : "s"}</button>}
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {!data ? <div className="py-12 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2.5">Client</th>
                  {SERVICE_KEYS.map((k) => <th key={k} className="px-2 py-2.5 text-center"><span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: serviceMeta(k).color }} />{serviceMeta(k).label}</span></th>)}
                  <th className="px-2 py-2.5 text-right">Total</th>
                  <th className="px-3 py-2.5">Applies to</th>
                  {canManage && <th className="w-24 px-3 py-2.5" />}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? <tr><td colSpan={SERVICE_KEYS.length + 4} className="py-10 text-center text-slate-500">No clients have targets yet. Tick “Show clients without targets” to add some.</td></tr>
                  : rows.map((c) => {
                    const changed = !!edits[c.client_id];
                    const total = SERVICE_KEYS.reduce((s, k) => s + (Number(valueOf(c, k)) || 0), 0);
                    return (
                      <tr key={c.client_id} className={`border-t border-slate-100 ${changed ? "bg-amber-50/40" : c.client_id === highlightClientId ? "bg-emerald-50/60" : ""}`}>
                        <td className="px-3 py-2 font-medium text-slate-900">{c.name}{c.client_id === highlightClientId && <span className="ml-1.5 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">New</span>}</td>
                        {SERVICE_KEYS.map((k) => (
                          <td key={k} className="px-2 py-1.5 text-center">
                            {canManage ? (
                              <input type="number" min="0" max="999" value={valueOf(c, k)} onChange={(e) => setVal(c, k, e.target.value)} data-testid={`mp-target-${c.client_id}-${k}`}
                                className="h-9 w-16 rounded-lg border border-slate-200 text-center text-sm tabular-nums focus:border-indigo-400 focus:outline-none" />
                            ) : <span className="tabular-nums">{c.targets[k]}</span>}
                          </td>
                        ))}
                        <td className="px-2 py-2 text-right font-semibold text-slate-900">{total}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{c.target_source === "month" ? `${monthLabel(month)} only` : c.target_source === "default" ? "Every month" : "—"}</td>
                        {canManage && (
                          <td className="px-3 py-2 text-right">
                            {changed && <button onClick={() => saveRow(c)} disabled={saving === c.client_id} className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700">
                              {saving === c.client_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
                            </button>}
                          </td>
                        )}
                      </tr>
                    );
                  })}
              </tbody>
              {rows.length > 0 && (
                <tfoot className="border-t-2 border-slate-200 bg-slate-50 text-xs font-semibold text-slate-700">
                  <tr>
                    <td className="px-3 py-2.5">Total</td>
                    {SERVICE_KEYS.map((k) => <td key={k} className="px-2 py-2.5 text-center tabular-nums">{colTotal(k)}</td>)}
                    <td className="px-2 py-2.5 text-right">{SERVICE_KEYS.reduce((s, k) => s + colTotal(k), 0)}</td>
                    <td colSpan={canManage ? 2 : 1} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
      <p className="flex items-start gap-1.5 text-xs text-slate-500">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Default targets apply to every month. A “this month only” target overrides the default for that month. Changing a target updates the dashboard immediately; it never deletes tasks — use “Generate Monthly Tasks” to create any missing task slots.
      </p>
    </div>
  );
}
